import React, { useState, useContext, useEffect, useMemo } from "react";
import { CalendarContext } from "../../../context/CalendarContext";
import { AuthContext } from "../../../context/AuthContext";
import { getSlotLanguageCodes, LANGUAGE_LABELS } from "../../../utils/languages";
import { matchSellerFromEmail } from "../../../utils/sellersMap";

const URI = process.env.REACT_APP_API_URL || "http://localhost:8080";
const SELLERS_API =
  "https://n8n.n8nmanguito.lat/webhook/cf78dc80-6f9a-4a5e-87f9-14a3a63d4135";
// Webhook de n8n que, al agendar, actualiza en HubSpot la descripcion del caso y
// la fecha/hora de la cita en el deal Y el lead (descripcion_de_caso /
// descripcion_del_caso / fecha__hora_de_la_cita), mas el contacto asociado
// (firstName/lastName/phone). Solo se llama si la reserva vino de un link de
// deal (`?id=`) o de lead (`?leadId=`).
const HUBSPOT_UPDATE_API =
  "https://n8n.n8nmanguito.lat/webhook/hubspot-deal-update";

const EventForm = ({
  eventDetails,
  setSeller,
  setRefNumber,
  setIsSubmitSuccessful,
  setIsSubmitted,
  setErrorMessage,
  setErrorTitleMessage,
}) => {
  const {
    setIsModalOpen,
    listAvailableTimes,
    resolvedDealId,
    hubspotLeadId,
    dealData,
  } = useContext(CalendarContext);
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    description: "",
    phone: "",
    seller: "",
    contactType: "",
    language: "",
  });

  // Idiomas disponibles en este slot, según los consultores libres.
  const availableLanguages = useMemo(
    () => getSlotLanguageCodes(eventDetails.consultants),
    [eventDetails.consultants]
  );

  // Si solo hay un idioma disponible, preseleccionarlo.
  useEffect(() => {
    if (availableLanguages.length === 1) {
      setFormData((prev) => ({ ...prev, language: availableLanguages[0] }));
    }
  }, [availableLanguages]);

  // Precarga los datos del deal de HubSpot (si el link traía `?id=`). Solo rellena
  // los campos que siguen vacíos, para no pisar lo que el usuario haya tipeado.
  useEffect(() => {
    if (!dealData) return;
    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName || dealData.firstName || "",
      lastName: prev.lastName || dealData.lastName || "",
      email: prev.email || dealData.email || "",
      phone: prev.phone || dealData.phone || "",
      description: prev.description || dealData.description || "",
    }));
  }, [dealData]);

  // El seller que completa el form es el usuario logueado. Derivamos su nombre
  // desde el email (nombre.inicialApellido) y lo matcheamos contra las opciones
  // del dropdown. Si no hay match, no se precarga nada.
  useEffect(() => {
    const matched = matchSellerFromEmail(user?.email, sellers);
    if (matched) {
      setFormData((prev) => ({ ...prev, seller: matched }));
    }
  }, [sellers, user]);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const res = await fetch(SELLERS_API);
        const data = await res.json();
        console.log("Sellers data:", data);

        // If your webhook returns { names: [...] }
        if (data.names && Array.isArray(data.names)) {
          setSellers([...data.names]);
        }
        // Or if it returns an array directly: [ "A", "B", ... ]
        else if (Array.isArray(data)) {
          setSellers([...data]);
        } else {
          console.warn("Unexpected sellers payload:", data);
        }
      } catch (error) {
        console.error("Failed to load sellers", error);
      }
    };

    fetchSellers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNotice("");
    if (name === "phone") {
      // Permite sólo números
      const re = /^[0-9\b]+$/;
      if (value === "" || re.test(value)) {
        setFormData((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    if (eventDetails.eventType === "virtual" && !formData.contactType) {
      setNotice("Please select a contact type.");
      setLoading(false);
      return;
    }
    if (!formData.language) {
      setNotice("Please select a language.");
      setLoading(false);
      return;
    }
    sendFormDataToAPI({ ...formData, eventDetails });
  };

  const sendFormDataToAPI = async (data) => {
    // Función que maneja el envío de los datos al API
    try {
      // Preparar el body de la solicitud
      const body = JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        description: data.description,
        phone: data.phone,
        contactType: data.contactType,
        eventDetails: {
          date: data.eventDetails.date,
          hour: data.eventDetails.hour,
          eventType: data.eventDetails.eventType,
          consultants: data.eventDetails.consultants, // Asegúrate de que esto se incluya correctamente desde el frontend
          language: data.language, // Idioma elegido: el backend filtra el consultor por idioma.
          flex: !!data.eventDetails.flex, // Lista de espera: va al calendario flex, sin consultor.
        },
      });

      // Realizar la solicitud POST
      const response = await fetch(`${URI}api/calendar/create-event`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: body,
      });

      const result = await response.json();

      if (!response.ok) {
        if (
          response.status === 409 ||
          (response.status === 404) | (response.status === 400)
        ) {
          const error = new Error(result.message || "Unknown error");
          error.titleMessage = result.titleMessage;
          throw error;
        }
        throw new Error("HTTP error, status = " + response.status);
      }

      // Datos para el webhook
      const webhookData = {
        id: result.event.id,
        nameConsultor: result.consultant,
        eventType: data.eventDetails.eventType,
        hora: data.eventDetails.hour,
        fecha: data.eventDetails.date,
        seller: formData.seller,
        idioma: data.language,
      };

      sendDataToWeebHook(webhookData);
      // Si la reserva vino de un link de deal de HubSpot, sincronizamos los
      // campos editados por el seller al deal y su contacto asociado.
      updateHubspotDeal(data);

      setLoading(false);
      setSeller(`${formData.seller}`);
      setRefNumber(`${result.event.id}`);
      setIsSubmitSuccessful(true);
      setIsSubmitted(true);
      listAvailableTimes();
      setErrorMessage("");
    } catch (error) {
      console.log(error);
      setLoading(false);
      setIsSubmitSuccessful(false);
      setIsSubmitted(true);
      setErrorMessage(error.message);
      listAvailableTimes();
      setErrorTitleMessage(error.titleMessage);
    }
  };

  const updateHubspotDeal = async (data) => {
    // Solo si la reserva vino de un link de deal (`?id=`) o de lead (`?leadId=`).
    // n8n actualiza deal y lead (resuelve el que falte por asociación). El email
    // NO se sincroniza a propósito, para no pisar el identificador del contacto.
    if (!resolvedDealId && !hubspotLeadId) return;
    try {
      await fetch(HUBSPOT_UPDATE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hubspotDealId: resolvedDealId || "",
          hubspotLeadId: hubspotLeadId || "",
          description: data.description,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          appointmentDate: data.eventDetails.date,
          appointmentHour: data.eventDetails.hour,
        }),
      });
    } catch (error) {
      console.log("HubSpot update failed", error);
    }
  };

  const sendDataToWeebHook = async (webhookData) => {
    // Enviar datos al webhook de n8n
    try {
      const response = await fetch(
        "https://n8n.n8nmanguito.lat/webhook/05192d16-c689-4a3c-9202-cb1a62b92cbe",
        {
          method: "POST",
          body: JSON.stringify(webhookData),
        }
      );
      const result = await response.json();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <form className="event-modal-form" onSubmit={handleSubmit}>
      <h2>{eventDetails.flex ? "Flex — lista de espera" : eventDetails.eventType}</h2>
      {eventDetails.flex && (
        <p className="flex-hint">
          El cliente queda en lista de espera, sin consultor asignado. Si se
          libera un horario por cancelación o no-show, el equipo lo ubica ese
          día. Avísele que el horario no está garantizado.
        </p>
      )}

      <div className="input-container">
        <label htmlFor={formData.firstName ? "lastName" : "firstName"}>
          Full Name
        </label>
        <div className="input-group">
          <input
            id="firstName"
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <input
            id="lastName"
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="input-container">
        <label htmlFor="email">Prospect email</label>
        <input
          id="email"
          type="email"
          name="email"
          placeholder="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>

      <div className="input-container">
        <label htmlFor="description">Describe your case</label>
        <textarea
          id="description"
          name="description"
          placeholder="Tell us about your case..."
          rows={6}
          value={formData.description}
          onChange={handleChange}
          required
        ></textarea>
      </div>

      <div className="input-container">
        <label htmlFor="seller">Select a Seller</label>
        <select
          id="seller"
          name="seller"
          value={formData.seller}
          onChange={handleChange}
          required
        >
          <option value="" disabled defaultValue="">
            Please select a seller
          </option>
          {sellers.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>

      <div className="input-container">
        <label htmlFor="language">Language</label>
        <select
          id="language"
          name="language"
          value={formData.language}
          onChange={handleChange}
          required
        >
          <option value="" disabled>
            Please select a language
          </option>
          {availableLanguages.map((code) => (
            <option key={code} value={code}>
              {LANGUAGE_LABELS[code]}
            </option>
          ))}
        </select>
      </div>

      {eventDetails.eventType === "virtual" && (
        <>
          <div className="checkbox-container">
            <div className="checkbox">
              <label htmlFor="phonecall">Phone Call</label>
              <input
                type="radio"
                id="phonecall"
                name="contactType"
                value="phone call"
                checked={formData.contactType === "phone call"}
                onChange={handleChange}
              />
            </div>
            <div className="checkbox">
              <label htmlFor="meet">Meet</label>
              <input
                type="radio"
                id="meet"
                name="contactType"
                value="meet"
                checked={formData.contactType === "meet"}
                onChange={handleChange}
              />
            </div>
          </div>
          <span className="error">{notice}</span>
        </>
      )}

      <div className="input-container">
        <label htmlFor="phone">Phone Number</label>
        <input
          id="phone"
          type="tel"
          name="phone"
          autoComplete="off"
          placeholder="Phone Number"
          value={formData.phone}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-btns-container">
        <button
          className="close"
          type="button"
          onClick={() => setIsModalOpen(false)}
        >
          Close
        </button>
        <button className="submit" type="submit" disabled={loading}>
          {loading ? "loading..." : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default EventForm;
